import { z, ZodError } from 'zod';

/**
 * Returns and validates the Supabase client keys from the environment.
 *
 * Note: callers (server clients, browser clients, edge runtime) DEPEND on
 * this function throwing when env vars are missing — do not silently
 * fall back. We DO augment the error message with VERCEL_ENV and
 * VERCEL_URL to make Sentry triage of misconfigured Vercel previews
 * fast (see BAL-169).
 *
 * The augmentation builds a NEW error and keeps the original as `cause`.
 * It must never assign to `err.message`: `ZodError.message` is a
 * prototype getter with no setter (zod v3 `ZodError.js`), so the
 * assignment itself throws `TypeError: Cannot set property message of
 * [object Object] which has only a getter` and replaces the one message
 * that names the missing variable with one that names nothing. Measured
 * 2026-09-04 (Sentry BALLEE-J1, 300+ events in 45 minutes): production
 * lost `NEXT_PUBLIC_SUPABASE_ANON_KEY` and every page 500'd behind that
 * TypeError while the real cause was one env var. zod 4 gives each error an
 * own `message` accessor with a setter, so the assignment no longer throws;
 * building a new error stays correct under both versions.
 */
export function getSupabaseClientKeys() {
  try {
    return z
      .object({
        // `.min(1)`: an empty value is as unusable as an absent one (every
        // request 401s), so it must fail HERE with the variable's name, not
        // later with an opaque auth error.
        url: z
          .string({
            error: (issue) =>
              issue.input === undefined
                ? `Please provide the variable NEXT_PUBLIC_SUPABASE_URL`
                : undefined,
          })
          .describe(
            `This is the URL of your hosted Supabase instance. Please provide the variable NEXT_PUBLIC_SUPABASE_URL.`,
          )
          .min(1, `Please provide the variable NEXT_PUBLIC_SUPABASE_URL`),
        publicKey: z
          .string({
            error: (issue) =>
              issue.input === undefined
                ? `Please provide the variable NEXT_PUBLIC_SUPABASE_PUBLIC_KEY`
                : undefined,
          })
          .describe(
            `This is the public key provided by Supabase. It is a public key used client-side. Please provide the variable NEXT_PUBLIC_SUPABASE_PUBLIC_KEY.`,
          )
          .min(
            1,
            `Please provide the variable NEXT_PUBLIC_SUPABASE_PUBLIC_KEY`,
          ),
      })
      .parse({
        url: process.env.NEXT_PUBLIC_SUPABASE_URL,
        publicKey:
          process.env.NEXT_PUBLIC_SUPABASE_PUBLIC_KEY ||
          process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
      });
  } catch (err) {
    // Augment the error with deployment context so Sentry triage of
    // misconfigured Vercel previews / branch deploys is one glance instead
    // of guess-the-host. NEXT_PUBLIC_VERCEL_ENV is preferred on the
    // browser since VERCEL_ENV isn't exposed there.
    const vercelEnv =
      process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV ?? 'unknown';
    const vercelUrl =
      process.env.NEXT_PUBLIC_VERCEL_URL ?? process.env.VERCEL_URL ?? 'unknown';
    const context = `[env=${vercelEnv}, host=${vercelUrl}]`;

    // Name the missing variable(s) in plain text. ZodError.message is the
    // issues array as JSON, which is correct but slow to read in a Sentry
    // title; `issues` carries the `required_error` strings above.
    const detail =
      err instanceof ZodError
        ? err.issues
            .map(
              (issue) =>
                `${issue.path.join('.') || '(root)'}: ${issue.message}`,
            )
            .join('; ')
        : err instanceof Error
          ? err.message
          : String(err);

    throw new Error(
      `Failed to load Supabase client keys ${context}: ${detail}`,
      { cause: err },
    );
  }
}
